export interface MoveInput { x: number; z: number; }
export interface InputPreferences { readonly mouseSensitivity: number; readonly touchSensitivity: number; readonly invertY: boolean; }
export interface InputDiagnostics {
  readonly pointerLock: "locked" | "unlocked";
  readonly lastMouseEvent: string;
  readonly lastLeftClick: string;
  readonly lastAction: string;
  readonly lastActionResult: string;
  readonly blockReason: string;
  readonly movement: { x: number; y: number };
  readonly pressedKeys: readonly string[];
}
export function shouldQueuePrimaryAction(isMobile: boolean, pointerLocked: boolean): boolean { return isMobile || pointerLocked; }
export function shouldQueueWeaponCycle(button:number,isMobile:boolean,gameplayActive:boolean):boolean{return button===2&&!isMobile&&gameplayActive;}
export function shouldRequestPointerLock(isMobile: boolean, pointerLocked: boolean): boolean { return !isMobile && !pointerLocked; }
export function mouseLookDelta(movementX: number, movementY: number, sensitivity: number): { yaw: number; pitch: number } { return { yaw: movementX * sensitivity, pitch: movementY * sensitivity }; }
export function lookSmoothingAlpha(deltaSeconds: number, response = 24): number { return 1 - Math.exp(-Math.max(0, deltaSeconds) * response); }
export function isEditableKeyboardTarget(target:EventTarget|null):boolean{return target instanceof HTMLElement&&(target.isContentEditable||["INPUT","TEXTAREA","SELECT"].includes(target.tagName));}

export class InputManager {
  readonly move: MoveInput = { x: 0, z: 0 };
  private readonly keys = new Set<string>();
  private yawDelta = 0;
  private pitchDelta = 0;
  private actionQueued = false;
  private primaryHeld=false;
  private interactQueued = false;
  private inventoryQueued = false;
  private hotkeyQueued?: number;
  private quickCaptureQueued=false;
  private cycleQueued = 0;
  private weaponCycleQueued = false;
  private gameplayActive = false;
  private pauseQueued = false;
  private pointerLockLost = false;
  private pointerWasLocked = false;
  private diagnosticsToggleQueued = false;
  private lastMouseEvent = "none";
  private lastLeftClick = "none";
  private lastAction = "none";
  private lastActionResult = "none";
  private blockReason = "none";
  private lastMovement = { x: 0, y: 0 };
  private touchLookId?: number;
  private lastTouchX = 0;
  private lastTouchY = 0;
  private moveTouchId?: number;
  private moveOriginX = 0;
  private moveOriginY = 0;
  private readonly touchMove: MoveInput = { x: 0, z: 0 };
  private mouseSensitivity = 0.0022;
  private touchSensitivity = 0.004;
  private invertY = false;

  constructor(private readonly canvas: HTMLCanvasElement, preferences: Partial<InputPreferences> = {}) {
    this.setPreferences(preferences);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup",this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("blur",this.onWindowBlur);
    canvas.addEventListener("contextmenu", this.onContextMenu);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("pointerlockerror", this.onPointerLockError);
    canvas.addEventListener("touchstart", this.onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", this.onTouchMove, { passive: false });
    canvas.addEventListener("touchend", this.onTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", this.onTouchEnd, { passive: false });
  }

  update(): void {
    this.move.x = Math.max(-1, Math.min(1, Number(this.keys.has("KeyD")) - Number(this.keys.has("KeyA")) + this.touchMove.x));
    this.move.z = Math.max(-1, Math.min(1, Number(this.keys.has("KeyW")) - Number(this.keys.has("KeyS")) + this.touchMove.z));
  }

  consumeLook(deltaSeconds = 1 / 60): { yaw: number; pitch: number } {
    const alpha = lookSmoothingAlpha(deltaSeconds);
    const value = { yaw: this.yawDelta * alpha, pitch: this.pitchDelta * alpha };
    this.yawDelta -= value.yaw; this.pitchDelta -= value.pitch;
    if (Math.abs(this.yawDelta) < 0.000001) this.yawDelta = 0;
    if (Math.abs(this.pitchDelta) < 0.000001) this.pitchDelta = 0;
    return value;
  }
  consumeAction(): boolean { const value = this.actionQueued; this.actionQueued = false; return value; }
  consumeInteract(): boolean { const value = this.interactQueued; this.interactQueued = false; return value; }
  consumeInventory(): boolean { const value = this.inventoryQueued; this.inventoryQueued = false; return value; }
  consumeHotkey(): number | undefined { const value = this.hotkeyQueued; this.hotkeyQueued = undefined; return value; }
  consumeQuickCapture():boolean{const value=this.quickCaptureQueued;this.quickCaptureQueued=false;return value;}
  consumeCycle(): number { const value = this.cycleQueued; this.cycleQueued = 0; return value; }
  consumeWeaponCycle():boolean{const value=this.weaponCycleQueued;this.weaponCycleQueued=false;return value;}
  consumePause(): boolean { const value = this.pauseQueued; this.pauseQueued = false; return value; }
  consumePointerLockLost(): boolean { const value = this.pointerLockLost; this.pointerLockLost = false; return value; }
  consumeDiagnosticsToggle(): boolean { const value=this.diagnosticsToggleQueued;this.diagnosticsToggleQueued=false;return value; }
  get isPointerLocked(): boolean { return document.pointerLockElement === this.canvas; }
  get primaryActionHeld():boolean{return this.primaryHeld;}
  get pointerLockStatus(): "locked" | "unlocked" { return this.isPointerLocked ? "locked" : "unlocked"; }

  queueAction(): void { this.actionQueued = true; }
  queueInteract(): void { this.interactQueued = true; }
  queueHotkey(value: number): void { this.hotkeyQueued = value; }
  setGameplayActive(active:boolean):void{this.gameplayActive=active;if(!active){this.weaponCycleQueued=false;this.primaryHeld=false;}}
  setVirtualMove(x: number, z: number): void { this.touchMove.x = x; this.touchMove.z = z; }
  setPreferences(preferences: Partial<InputPreferences>): void {
    if (Number.isFinite(preferences.mouseSensitivity)) this.mouseSensitivity = Math.max(0.0008, Math.min(0.006, preferences.mouseSensitivity!));
    if (Number.isFinite(preferences.touchSensitivity)) this.touchSensitivity = Math.max(0.0015, Math.min(0.01, preferences.touchSensitivity!));
    if (typeof preferences.invertY === "boolean") this.invertY = preferences.invertY;
  }
  resetTransientInput(): void { this.keys.clear();this.yawDelta=0;this.pitchDelta=0;this.actionQueued=false;this.primaryHeld=false;this.interactQueued=false;this.inventoryQueued=false;this.hotkeyQueued=undefined;this.quickCaptureQueued=false;this.cycleQueued=0;this.weaponCycleQueued=false;this.pauseQueued=false;this.pointerLockLost=false;this.touchMove.x=0;this.touchMove.z=0;this.touchLookId=undefined;this.moveTouchId=undefined; }
  recordAction(action: string, result: string, blockReason = "none"): void { this.lastAction=action;this.lastActionResult=result;this.blockReason=blockReason; }
  get diagnostics(): InputDiagnostics { return {pointerLock:this.pointerLockStatus,lastMouseEvent:this.lastMouseEvent,lastLeftClick:this.lastLeftClick,lastAction:this.lastAction,lastActionResult:this.lastActionResult,blockReason:this.blockReason,movement:{...this.lastMovement},pressedKeys:[...this.keys].sort()}; }

  requestPointerLock(): void {
    if (!shouldRequestPointerLock(this.isMobile(), this.isPointerLocked)) return;
    this.canvas.focus({preventScroll:true});
    try {
      const request = this.canvas.requestPointerLock();
      if (request && typeof request.catch === "function") void request.catch((error: unknown) => { this.blockReason=`pointer lock rejected: ${error instanceof Error?error.name:"unknown"}`; });
    } catch (error) { this.blockReason=`pointer lock error: ${error instanceof Error?error.name:"unknown"}`; }
  }

  isMobile(): boolean { return matchMedia("(pointer: coarse)").matches; }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup",this.onMouseUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("blur",this.onWindowBlur);
    this.canvas.removeEventListener("contextmenu", this.onContextMenu);
    this.canvas.removeEventListener("wheel", this.onWheel);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    document.removeEventListener("pointerlockerror", this.onPointerLockError);
    this.canvas.removeEventListener("touchstart", this.onTouchStart);
    this.canvas.removeEventListener("touchmove", this.onTouchMove);
    this.canvas.removeEventListener("touchend", this.onTouchEnd);
    this.canvas.removeEventListener("touchcancel", this.onTouchEnd);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if(isEditableKeyboardTarget(event.target))return;
    if(this.gameplayActive)this.keys.add(event.code);
    if(this.gameplayActive&&/^(?:Digit|Numpad)[0-9]$/.test(event.code))this.hotkeyQueued=event.code.endsWith("0")?10:Number(event.code.at(-1));
    if(this.gameplayActive&&event.code==="Space"){event.preventDefault();if(!event.repeat)this.quickCaptureQueued=true;}
    if (this.gameplayActive&&event.code === "KeyE") this.interactQueued = true;
    if (this.gameplayActive&&event.code === "Tab") { event.preventDefault(); this.inventoryQueued = true; }
    if (this.gameplayActive&&event.code === "Escape" && document.pointerLockElement !== this.canvas) this.pauseQueued = true;
    if (import.meta.env.DEV && event.code === "F9" && !event.repeat) { event.preventDefault();this.diagnosticsToggleQueued=true; }
  };
  private readonly onKeyUp = (event: KeyboardEvent): void => { this.keys.delete(event.code); };
  private readonly onMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.primaryHeld=true;
      const locked=this.isPointerLocked;this.lastLeftClick=`button 0 · ${locked?"locked":"unlocked"}`;
      if (shouldQueuePrimaryAction(this.isMobile(), locked)) { this.actionQueued = true;this.lastAction="primary queued";this.blockReason="none"; }
      else { this.blockReason="pointer lock required";this.requestPointerLock(); }
    }else if(shouldQueueWeaponCycle(event.button,this.isMobile(),this.gameplayActive)){this.weaponCycleQueued=true;this.lastAction="weapon cycle queued";this.blockReason="none";}
  };
  private readonly onMouseUp=(event:MouseEvent):void=>{if(event.button===0)this.primaryHeld=false;};
  private readonly onWindowBlur=():void=>{this.primaryHeld=false;};
  private readonly onMouseMove = (event: MouseEvent): void => {
    this.lastMovement={x:event.movementX,y:event.movementY};this.lastMouseEvent=`move ${event.movementX},${event.movementY}`;
    if (document.pointerLockElement === this.canvas) {
      const delta = mouseLookDelta(event.movementX, event.movementY, this.mouseSensitivity);
      this.yawDelta += delta.yaw;
      this.pitchDelta += this.invertY ? -delta.pitch : delta.pitch;
    }
  };
  private readonly onContextMenu = (event: Event): void => { if(this.gameplayActive)event.preventDefault(); };
  private readonly onWheel = (event: WheelEvent): void => { event.preventDefault(); this.cycleQueued = Math.sign(event.deltaY); };
  private readonly onPointerLockChange = (): void => {
    const locked=document.pointerLockElement===this.canvas;
    if(this.pointerWasLocked&&!locked)this.pointerLockLost=true;
    this.pointerWasLocked=locked;
    this.blockReason=locked?"none":"pointer lock inactive";
  };
  private readonly onPointerLockError = (): void => { this.blockReason="browser denied pointer lock";this.lastActionResult="control lock failed"; };
  private readonly onTouchStart = (event: TouchEvent): void => {
    event.preventDefault();
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.clientX < innerWidth * 0.48 && this.moveTouchId === undefined) {
        this.moveTouchId = touch.identifier; this.moveOriginX = touch.clientX; this.moveOriginY = touch.clientY;
      } else if (this.touchLookId === undefined) {
        this.touchLookId = touch.identifier; this.lastTouchX = touch.clientX; this.lastTouchY = touch.clientY;
      }
    }
  };
  private readonly onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.identifier === this.moveTouchId) {
        this.touchMove.x = Math.max(-1, Math.min(1, (touch.clientX - this.moveOriginX) / 55));
        this.touchMove.z = Math.max(-1, Math.min(1, (this.moveOriginY - touch.clientY) / 55));
      } else if (touch.identifier === this.touchLookId) {
        this.yawDelta += (touch.clientX - this.lastTouchX) * this.touchSensitivity;
        const pitch = (touch.clientY - this.lastTouchY) * this.touchSensitivity;
        this.pitchDelta += this.invertY ? -pitch : pitch;
        this.lastTouchX = touch.clientX; this.lastTouchY = touch.clientY;
      }
    }
  };
  private readonly onTouchEnd = (event: TouchEvent): void => {
    event.preventDefault();
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.identifier === this.moveTouchId) { this.moveTouchId = undefined; this.touchMove.x = 0; this.touchMove.z = 0; }
      if (touch.identifier === this.touchLookId) this.touchLookId = undefined;
    }
  };
}
